import type { Maybe } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { html, nothing, type TemplateResult } from 'lit';
import { enrollUserInCourse, getEnrollmentsForCourse, removeEnrollment, resetEnrollment } from '../api/lms-queries.js';
import { type LMSCourseEnrollment, LMSEnrollmentStatus } from '../entities/LMSCourseEnrollment.js';
import { clampScorePercent } from './score-utils.js';

/** A completed attempt reduced to what the Score column needs. */
export interface EnrollmentAttemptVM {
  scorePercent: Maybe<number>;
  passed: Maybe<boolean>;
  /** Client IP the attempt was started from; null for attempts recorded before IP capture. */
  ip?: Maybe<string>;
}

/** One row of the Enrollments table, pre-resolved so the table renders without per-row lookups. */
export interface EnrollmentRowVM {
  enrollmentId: string;
  userId: string | null;
  /** Pre-resolved display name. When null the manager lazily resolves it per row (generic fallback). */
  name: string | null;
  /** Muted secondary line under the name (e.g. the user's email). */
  subtitle?: string | null;
  status: LMSEnrollmentStatus;
  lockedAt: Date | null;
  /** Completed attempts in chronological order. */
  completedAttempts: EnrollmentAttemptVM[];
  firstPassedAt: Date | null;
  /** True when any attempt has been completed — the enrollment can then no longer be deleted. */
  hasCompletedAttempt: boolean;
}

export interface EnrollmentPageResult {
  rows: EnrollmentRowVM[];
  total: number;
}

/** Loads one page of enrollments. Injected by the host so search/pagination can happen server-side. */
export interface EnrollmentDataSource {
  loadPage(opts: { courseId: string; search: string; skip: number; limit: number }): Promise<EnrollmentPageResult>;
}

export interface EnrollmentManagerProps {
  courseId: string;
  /** Custom picker component for enrolling new users. Should call onUserPicked with the userId when a user is selected. */
  renderEnrollmentPicker?: (opts: { onUserPicked: (userId: string) => void }) => TemplateResult;
  /** Custom renderer for the enrolled user's name, used only when a row has no pre-resolved `name`. */
  renderEnrolledUserName?: (opts: { userId: string }) => TemplateResult;
  /** Custom formatter for the "First passed at" date. Defaults to the browser locale. */
  formatDate?: (date: Date | null) => string;
  /** Rows per page. Defaults to 100. */
  pageSize?: number;
  /** Server-side page loader. Defaults to a generic loader over {@link getEnrollmentsForCourse}. */
  dataSource?: EnrollmentDataSource;
}

const DEFAULT_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 300;

const UserName = stateful(({ userId }: { userId?: string | null }) => {
  const { value: user } = useQuery(
    () => (userId ? getByID(AdornisUser, userId)({ _id: 1, username: 1, email: 1 }) : Promise.resolve(null)),
    [userId],
  );
  const resolved = user as AdornisUser | null;
  return html`${resolved?.username ?? resolved?.email ?? userId ?? '–'}`;
});

const DefaultEnrollmentPicker = ({ onUserPicked }: { onUserPicked: (userId: string) => void }) =>
  html`${EntityPickerInput<typeof AdornisUser>({
    class: AdornisUser,
    filter: new AdornisFilter({}),
    selectionSet: { _id: 1, username: 1, email: 1 },
    placeholder: 'Assign user',
    emptyState: 'No more users available',
    renderString: u => u.username,
    onEntityPicked: user => {
      if (user?._id) onUserPicked(user._id);
    },
  })}`;

const statusLabel = (status: Maybe<LMSEnrollmentStatus>) => {
  switch (status) {
    case LMSEnrollmentStatus.PASSED:
      return html`<span class="badge badge-success badge-sm">Passed</span>`;
    case LMSEnrollmentStatus.FAILED:
      return html`<span class="badge badge-error badge-sm">Failed</span>`;
    case LMSEnrollmentStatus.IN_PROGRESS:
      return html`<span class="badge badge-warning badge-sm">In Progress</span>`;
    default:
      return html`<span class="badge badge-info badge-sm">Assigned</span>`;
  }
};

const getFirstPassedAt = (enrollment: LMSCourseEnrollment): Date | null => {
  const passedAttemptDates = (enrollment.attempts ?? [])
    .filter(attempt => attempt.passed && attempt.completedAt)
    .map(attempt => new Date(attempt.completedAt as Date));
  if (passedAttemptDates.length === 0) return null;

  return passedAttemptDates.reduce((earliest, current) => (current < earliest ? current : earliest));
};

const defaultDisplayDate = (date: Date | null): string => (date ? date.toLocaleDateString() : '–');

/** Maps a full enrollment entity to a row VM (name unresolved — the table resolves it lazily). */
const enrollmentToVM = (enrollment: LMSCourseEnrollment): EnrollmentRowVM => {
  const attempts = enrollment.attempts ?? [];
  const completed = attempts.filter(a => a.completedAt);
  return {
    enrollmentId: enrollment._id,
    userId: enrollment.userId ?? null,
    name: null,
    subtitle: null,
    status: enrollment.status,
    lockedAt: enrollment.lockedAt ?? null,
    completedAttempts: completed.map(a => ({ scorePercent: a.scorePercent, passed: a.passed, ip: a.ip })),
    firstPassedAt: getFirstPassedAt(enrollment),
    hasCompletedAttempt: completed.length > 0,
  };
};

/**
 * Generic fallback data source: fetches every enrollment for the course and paginates/searches on
 * the client. Search matches the user id only (this source cannot resolve names). Hosts that need
 * name/email search should inject a server-side {@link EnrollmentDataSource}.
 */
const defaultDataSource: EnrollmentDataSource = {
  async loadPage({ courseId, search, skip, limit }) {
    const all = (await getEnrollmentsForCourse(courseId)({
      _id: 1,
      userId: 1,
      lockedAt: 1,
      createdAt: 1,
      attempts: { scorePercent: 1, completedAt: 1, passed: 1, ip: 1 },
    })) as LMSCourseEnrollment[];
    const needle = search.toLowerCase();
    const filtered = needle ? all.filter(e => (e.userId ?? '').toLowerCase().includes(needle)) : all;
    return { rows: filtered.slice(skip, skip + limit).map(enrollmentToVM), total: filtered.length };
  },
};

/** Compact list of page indices to render, collapsing long gaps to '…'. All values are 0-based. */
const buildPageList = (current: number, count: number): (number | '…')[] => {
  const wanted = new Set<number>([0, count - 1]);
  for (let p = current - 2; p <= current + 2; p++) if (p >= 0 && p < count) wanted.add(p);
  const sorted = [...wanted].filter(p => p >= 0 && p < count).sort((a, b) => a - b);
  const out: (number | '…')[] = [];
  let prev = -1;
  for (const p of sorted) {
    if (prev >= 0 && p - prev > 1) out.push('…');
    out.push(p);
    prev = p;
  }
  return out;
};

export const CourseEnrollmentManager = stateful(
  ({
    courseId,
    renderEnrollmentPicker,
    renderEnrolledUserName,
    formatDate,
    pageSize = DEFAULT_PAGE_SIZE,
    dataSource = defaultDataSource,
  }: EnrollmentManagerProps) => {
    const displayDate = formatDate ?? defaultDisplayDate;
    const [refreshKey, setRefreshKey] = useState(0);
    const [page, setPage] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    // Debounce the search box: apply after the user stops typing, and jump back to the first page.
    useEffect(() => {
      const handle = setTimeout(() => {
        setSearch(searchInput.trim());
        setPage(0);
      }, SEARCH_DEBOUNCE_MS);
      return () => clearTimeout(handle);
    }, [searchInput]);

    const pageQuery = useQuery(
      () => dataSource.loadPage({ courseId, search, skip: page * pageSize, limit: pageSize }),
      [courseId, search, page, pageSize, refreshKey],
    );

    const rows = pageQuery.value?.rows ?? [];
    const total = pageQuery.value?.total ?? 0;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));

    if (pageQuery.error) {
      return html`<p class="text-sm text-error">Error loading enrollments: ${pageQuery.error.message}</p>`;
    }

    const handleEnroll = async (userId: string) => {
      await guardDialog(
        async () => {
          await enrollUserInCourse(courseId, userId)({ _id: 1 });
          setRefreshKey(refreshKey + 1);
        },
        {
          loadingText: 'User is being enrolled…',
          successText: 'User enrolled.',
          errorMessagePrefix: 'Enrollment failed',
        },
      );
    };

    const handleRemove = async (enrollmentId: string) => {
      await guardDialog(
        async () => {
          await removeEnrollment(enrollmentId)();
          setRefreshKey(refreshKey + 1);
        },
        {
          loadingText: 'Enrollment is being removed…',
          successText: 'Enrollment removed.',
          errorMessagePrefix: 'Removal failed',
        },
      );
    };

    const handleReset = async (enrollmentId: string) => {
      await guardDialog(
        async () => {
          await resetEnrollment(enrollmentId)({ _id: 1, lockedAt: 1 });
          setRefreshKey(refreshKey + 1);
        },
        {
          loadingText: 'Enrollment is being reset…',
          successText: 'Enrollment reset and unlocked.',
          errorMessagePrefix: 'Reset failed',
        },
      );
    };

    const nameCell = (row: EnrollmentRowVM) =>
      row.name != null
        ? html`
            <div class="flex flex-col">
              <span>${row.name}</span>
              ${row.subtitle ? html`<span class="text-xs text-base-content/50">${row.subtitle}</span>` : nothing}
            </div>
          `
        : (renderEnrolledUserName?.({ userId: row.userId ?? '' }) ?? UserName({ userId: row.userId }));

    const paginationControls = () => {
      if (pageCount <= 1) return nothing;
      return html`
        <div class="flex items-center justify-between flex-wrap gap-2">
          <span class="text-xs text-base-content/50">
            Page ${page + 1} of ${pageCount}${pageQuery.loading ? html` · loading…` : nothing}
          </span>
          <div class="join">
            <button
              class="join-item btn btn-sm"
              ?disabled=${page === 0}
              @click=${() => setPage(page - 1)}
            >
              «
            </button>
            ${buildPageList(page, pageCount).map(entry =>
              entry === '…'
                ? html`<button class="join-item btn btn-sm btn-disabled">…</button>`
                : html`
                    <button
                      class="join-item btn btn-sm ${entry === page ? 'btn-active' : ''}"
                      @click=${() => setPage(entry)}
                    >
                      ${entry + 1}
                    </button>
                  `,
            )}
            <button
              class="join-item btn btn-sm"
              ?disabled=${page >= pageCount - 1}
              @click=${() => setPage(page + 1)}
            >
              »
            </button>
          </div>
        </div>
      `;
    };

    return html`
      <div class="flex flex-col gap-3 w-full">
        <div class="flex items-end gap-2 flex-wrap">
          ${(renderEnrollmentPicker ?? DefaultEnrollmentPicker)({
            onUserPicked: userId => {
              void handleEnroll(userId);
            },
          })}
          <label class="input input-sm input-bordered flex items-center gap-2">
            <span class="icon-[mdi--magnify] text-base opacity-60"></span>
            <input
              type="search"
              class="grow"
              placeholder="Search by name or email"
              .value=${searchInput}
              @input=${(e: Event) => setSearchInput((e.target as HTMLInputElement).value)}
            />
          </label>
        </div>

        ${pageQuery.loading && rows.length === 0
          ? html`<span class="loading loading-spinner loading-sm"></span>`
          : rows.length === 0
            ? html`<p class="text-sm text-base-content/50">
                ${search ? 'No enrollments match your search.' : 'No enrollments yet.'}
              </p>`
            : html`
                ${paginationControls()}
                <div class="overflow-x-auto">
                  <table class="table table-sm">
                    <thead>
                      <tr>
                        <th>User (${total})</th>
                        <th>Status</th>
                        <th>Score</th>
                        <th>First passed at</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      ${rows.map(
                        row => html`
                          <tr>
                            <td class="font-medium">${nameCell(row)}</td>
                            <td>${statusLabel(row.status)}</td>
                            <td>
                              ${row.lockedAt
                                ? html`
                                    <div class="mb-1">
                                      <span class="badge badge-error badge-xs">Locked</span>
                                    </div>
                                  `
                                : ''}
                              ${row.completedAttempts.length > 0
                                ? html`
                                    <div class="text-sm">
                                      ${row.completedAttempts.map(
                                        attempt =>
                                          html`<div class="flex items-center gap-1">
                                            <span>${clampScorePercent(attempt.scorePercent)}%</span>
                                            ${attempt.passed
                                              ? html`<span class="badge badge-success badge-xs">✓</span>`
                                              : html`<span class="badge badge-error badge-xs">✗</span>`}
                                            ${attempt.ip
                                              ? html`<span class="text-xs text-base-content/40 font-mono">${attempt.ip}</span>`
                                              : nothing}
                                          </div>`,
                                      )}
                                    </div>
                                  `
                                : html`<span class="text-sm text-base-content/40">–</span>`}
                            </td>
                            <td class="text-sm text-base-content/70">${displayDate(row.firstPassedAt)}</td>
                            <td>
                              ${row.hasCompletedAttempt
                                ? html`
                                    <span
                                      class="tooltip tooltip-left"
                                      data-tip="Kann nicht gelöscht werden (Versuche vorhanden)"
                                    >
                                      <button class="btn btn-ghost btn-xs btn-square text-base-content/20" disabled>
                                        <span class="icon-[mdi--delete-outline] text-base"></span>
                                      </button>
                                    </span>
                                  `
                                : html`
                                    <button
                                      class="btn btn-ghost btn-xs btn-square text-error"
                                      @click=${() => handleRemove(row.enrollmentId)}
                                    >
                                      <span class="icon-[mdi--delete-outline] text-base"></span>
                                    </button>
                                  `}
                              <button
                                class="btn btn-ghost btn-xs btn-square text-warning"
                                @click=${() => handleReset(row.enrollmentId)}
                              >
                                <span class="icon-[mdi--refresh] text-base"></span>
                              </button>
                            </td>
                          </tr>
                        `,
                      )}
                    </tbody>
                  </table>
                </div>
                ${paginationControls()}
              `}
      </div>
    `;
  },
);
